import { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react'

import { Config } from '@/types/config'

export interface ConfigContextProps {
  config: Config | undefined
  setConfig: Dispatch<SetStateAction<Config | undefined>>
}

export const ConfigContext = createContext({} as ConfigContextProps)

interface ConfigProviderProps {
  children: ReactNode
}

export const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const [config, setConfig] = useState<ConfigContextProps['config']>(() => {
    const lsConfig = localStorage.getItem('config')
    return lsConfig ? (JSON.parse(lsConfig) as Config) : undefined
  })

  const customThemeAttributes = useRef<Record<string, string>>({})

  const setAttribute = (prev: string, key: string, value: string) => {
    const attribute = `${prev}-${key}`
    customThemeAttributes.current[attribute] = value
  }

  const parseAttributes = (theme: any, prev: string) => {
    Object.entries(theme).forEach(([key, value]) => {
      if (typeof value === 'string') {
        setAttribute(prev, key, value)
      } else {
        // Recursively parse attributes
        parseAttributes(value, `${prev}-${key}`)
      }
    })
  }

  useEffect(() => {
    if (!config?.theme) return

    // Reset custom theme
    customThemeAttributes.current = {}
    const styleTag = document.getElementById('custom-theme')
    styleTag?.remove()

    // Parse theme attributes
    parseAttributes(config.theme, '--theme')

    // Create custom theme style tag and append to head
    const style = document.createElement('style')
    style.id = 'custom-theme'
    style.innerHTML = `:root{${Object.entries(customThemeAttributes.current)
      .map(([key, value]) => `${key}:${value};`)
      .join('')}}`
    document.head.appendChild(style)
  }, [config?.theme])

  useEffect(() => {
    if (!config?.meta) return

    const title = config.meta.title
    const description = config.meta.description

    if (title) document.title = title
    if (description) document.querySelector('meta[name="description"]')?.setAttribute('content', description)
  }, [config?.meta])

  const value = {
    config,
    setConfig
  }

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
}
